/**
 * Unified Image Optimization Engine
 * Consolidates all image optimization logic into a single, powerful system
 */

import { supabase } from '@/integrations/supabase/client';
import { ImageQualityLevel, OptimizedImageOptions, ImageTransformParams } from '@/hooks/image-optimization/types';
import { getImageQualitySettings, isMobileDevice } from '@/hooks/image-optimization/device-detection';
import { getBestSupportedFormat } from '@/hooks/image-optimization/format-detection';

interface OptimizationProfile {
  name: string;
  quality: number;
  format: string;
  maxWidth: number;
  enableAvif: boolean;
  aggressiveCompression: boolean;
}

interface OptimizationResult {
  optimizedUrl: string;
  srcSet: string;
  avifSrcSet?: string;
  placeholderUrl?: string;
  estimatedSavings: number;
  format: string;
  originalSize?: number;
  optimizedSize?: number;
}

interface CacheEntry {
  url: string;
  timestamp: number;
  profile: string;
}

export class ImageOptimizationEngine {
  private static instance: ImageOptimizationEngine;
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_TTL = 3600000; // 1 hour
  private readonly MAX_CACHE_SIZE = 1000;

  // Optimization profiles for different use cases
  private readonly profiles: Record<string, OptimizationProfile> = {
    hero: {
      name: 'hero',
      quality: 85,
      format: 'webp',
      maxWidth: 1200,
      enableAvif: true,
      aggressiveCompression: false
    },
    gallery: {
      name: 'gallery',
      quality: 80,
      format: 'webp',
      maxWidth: 800,
      enableAvif: true,
      aggressiveCompression: true
    },
    thumbnail: {
      name: 'thumbnail',
      quality: 75,
      format: 'webp',
      maxWidth: 400,
      enableAvif: true,
      aggressiveCompression: true
    },
    mobile: {
      name: 'mobile',
      quality: 70,
      format: 'webp',
      maxWidth: 600,
      enableAvif: false, // Faster encoding for mobile
      aggressiveCompression: true
    },
    lowBandwidth: {
      name: 'lowBandwidth',
      quality: 60,
      format: 'webp',
      maxWidth: 480,
      enableAvif: false,
      aggressiveCompression: true
    }
  };

  public static getInstance(): ImageOptimizationEngine {
    if (!ImageOptimizationEngine.instance) {
      ImageOptimizationEngine.instance = new ImageOptimizationEngine();
    }
    return ImageOptimizationEngine.instance;
  }

  /**
   * Get the optimal profile based on device and network conditions
   */
  private getOptimalProfile(options: Partial<OptimizedImageOptions>): OptimizationProfile {
    const isMobile = isMobileDevice();
    const connection = (navigator as any)?.connection;
    const isSlowConnection = connection && ['slow-2g', '2g', '3g'].includes(connection.effectiveType);
    const isSaveData = connection?.saveData;

    // Override for specific use cases
    if (options.width && options.width > 800) {
      return this.profiles.hero;
    }

    // Adaptive selection based on conditions
    if (isSaveData || isSlowConnection) {
      return this.profiles.lowBandwidth;
    }

    if (isMobile) {
      return this.profiles.mobile;
    }

    if (options.width && options.width <= 400) {
      return this.profiles.thumbnail;
    }

    return this.profiles.gallery;
  }

  /**
   * Generate optimized image URL with intelligent format selection
   */
  private generateOptimizedUrl(src: string, params: ImageTransformParams, profile: OptimizationProfile): string {
    if (!src.includes('lovable-uploads')) {
      return src;
    }

    const urlParams = new URLSearchParams();
    
    // Use profile settings with parameter overrides
    urlParams.set('format', params.format || profile.format);
    urlParams.set('width', Math.min(params.width, profile.maxWidth).toString());
    
    if (params.height) {
      urlParams.set('height', params.height.toString());
    }
    
    urlParams.set('quality', (params.quality || profile.quality).toString());
    
    if (profile.aggressiveCompression) {
      urlParams.set('compress', 'true');
    }

    return `${src}?${urlParams.toString()}`;
  }

  /**
   * Generate responsive srcSet with optimal breakpoints
   */
  private generateSrcSet(src: string, profile: OptimizationProfile, format: string): string {
    if (!src.includes('lovable-uploads')) {
      return src;
    }

    const breakpoints = [320, 480, 640, 800, 1024, 1200].filter(w => w <= profile.maxWidth);
    
    return breakpoints.map(width => {
      const quality = Math.max(profile.quality - (width > 800 ? 0 : 5), 60);
      const url = this.generateOptimizedUrl(src, { 
        width, 
        quality, 
        format 
      }, profile);
      return `${url} ${width}w`;
    }).join(', ');
  }

  /**
   * Generate placeholder image for progressive loading
   */
  private generatePlaceholder(src: string, size: number = 10): string {
    if (!src.includes('lovable-uploads')) {
      return src;
    }

    return this.generateOptimizedUrl(src, {
      width: size,
      quality: 20,
      format: 'webp'
    }, this.profiles.thumbnail);
  }

  /**
   * Optimize image using TinyPNG edge function with caching
   */
  public async optimizeWithTinyPng(src: string, options: Partial<OptimizedImageOptions>): Promise<string> {
    const cacheKey = `tinypng-${src}-${JSON.stringify(options)}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.url;
    }

    try {
      const { data, error } = await supabase.functions.invoke('optimize-image', {
        body: {
          imageUrl: src,
          width: options.width,
          height: options.height,
          quality: options.quality,
          format: options.format
        }
      });

      if (error) throw error;

      if (data?.optimizedUrl && !data.fallback) {
        this.cache.set(cacheKey, {
          url: data.optimizedUrl,
          timestamp: Date.now(),
          profile: 'tinypng'
        });
        
        // Clean cache if too large
        if (this.cache.size > this.MAX_CACHE_SIZE) {
          this.cleanCache();
        }
        
        return data.optimizedUrl;
      }
    } catch (error) {
      console.warn('TinyPNG optimization failed:', error);
    }

    return src; // Fallback to original
  }

  /**
   * Main optimization method that returns complete optimization result
   */
  public async optimize(src: string, options: Partial<OptimizedImageOptions> = {}): Promise<OptimizationResult> {
    // Get optimal profile and settings
    const profile = this.getOptimalProfile(options);
    const qualityLevel: ImageQualityLevel = options.quality && options.quality > 80 ? 'high' : 
                                           options.quality && options.quality < 70 ? 'low' : 'medium';
    
    const settings = getImageQualitySettings(qualityLevel, {
      width: options.width || 600,
      height: options.height
    });

    // Determine best format
    const bestFormat = getBestSupportedFormat(options.format || 'webp');
    const avifFormat = profile.enableAvif ? getBestSupportedFormat('avif') : null;

    // Generate URLs
    const optimizedUrl = this.generateOptimizedUrl(src, {
      ...settings,
      format: bestFormat
    }, profile);

    const srcSet = this.generateSrcSet(src, profile, bestFormat);
    const avifSrcSet = avifFormat ? this.generateSrcSet(src, profile, 'avif') : undefined;
    
    let placeholderUrl: string | undefined;
    if (options.placeholder !== false) {
      placeholderUrl = this.generatePlaceholder(src, options.placeholderSize);
    }

    // Estimate savings based on profile
    const estimatedSavings = profile.aggressiveCompression ? 70 : 
                           bestFormat === 'avif' ? 60 : 
                           bestFormat === 'webp' ? 50 : 30;

    return {
      optimizedUrl,
      srcSet,
      avifSrcSet,
      placeholderUrl,
      estimatedSavings,
      format: bestFormat
    };
  }

  /**
   * Batch optimize multiple images
   */
  public async batchOptimize(images: Array<{ src: string; options?: Partial<OptimizedImageOptions> }>): Promise<OptimizationResult[]> {
    const promises = images.map(({ src, options }) => this.optimize(src, options));
    return Promise.all(promises);
  }

  /**
   * Get optimization statistics
   */
  public getStats() {
    return {
      cacheSize: this.cache.size,
      cacheHitRate: this.cache.size > 0 ? 'Available' : 'Empty',
      supportedFormats: {
        webp: getBestSupportedFormat('webp') === 'webp',
        avif: getBestSupportedFormat('avif') === 'avif'
      }
    };
  }

  /**
   * Clean old cache entries
   */
  private cleanCache() {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.CACHE_TTL) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache
   */
  public clearCache() {
    this.cache.clear();
  }

  /**
   * Preload critical images
   */
  public preloadCriticalImages(images: string[]) {
    images.forEach(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    });
  }
}

// Export singleton instance
export const imageOptimizer = ImageOptimizationEngine.getInstance();